/**
 * Capacity Chart Component
 * Bar chart showing capacity vs allocation per team member
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { UserCapacity } from '../types';
import { getUtilizationColor, formatPercent } from '../utils/format';

interface CapacityChartProps {
  data: UserCapacity[];
  height?: number;
}

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
        <p className="font-semibold">{data.user.name}</p>
        <p className="text-sm text-gray-600">
          Allocated: {payload[0].value.toFixed(1)}h
        </p>
        <p className="text-sm text-gray-600">
          Available: {payload[1].value.toFixed(1)}h
        </p>
        <p className="text-sm font-semibold mt-2">
          Utilization: {formatPercent(data.utilizationPercent)}
        </p>
      </div>
    );
  }
  return null;
};

export const CapacityChart: React.FC<CapacityChartProps> = ({ data, height = 400 }) => {
  const chartData = data.map(member => ({
    name: member.user.name,
    allocated: member.currentAllocationHours,
    available: Math.max(0, member.availableCapacityHours),
    user: member.user,
    utilizationPercent: member.utilizationPercent,
    allocationStatus: member.allocationStatus,
  }));

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
          <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="allocated" stackId="capacity" fill="#ef4444" name="Allocated" />
          <Bar dataKey="available" stackId="capacity" fill="#22c55e" name="Available" />
        </BarChart>
      </ResponsiveContainer>

      {/* Status indicators */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        {[
          { status: 'over-allocated', label: 'Over-allocated', color: 'text-danger-600' },
          { status: 'at-capacity', label: 'At Capacity', color: 'text-warning-600' },
          { status: 'under-allocated', label: 'Under-allocated', color: 'text-success-600' },
        ].map(({ status, label, color }) => {
          const count = chartData.filter(d => d.allocationStatus === status).length;
          return (
            <div key={status} className="text-center">
              <div className={`text-2xl font-bold ${color}`}>{count}</div>
              <p className="text-sm text-gray-600">{label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CapacityChart;
